import { useState, useEffect, useCallback, useRef } from 'react';

// 工具函数
export const lerp = (a, b, t) => a + (b - a) * t;
export const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
// smoothstep缓动函数 - 与原HTML完全一致
export const ease = (p) => p * p * (3 - 2 * p);
export const damp = (current, target, speed, dt) => lerp(current, target, 1 - Math.exp(-speed * dt));

// 检测是否移动端或开启减弱动画（匹配原 HTML 的 900px 断点）
export const isMobile = () => typeof window !== 'undefined' && window.innerWidth <= 900;
export const isReduced = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// 获取元素在文档中的顶部位置
export const docTop = (el) => {
  if (!el) return 0;
  const rect = el.getBoundingClientRect();
  return rect.top + window.scrollY;
};

// 滚动进度计算 Hook
export function useScrollProgress(ref, options = {}) {
  const { offset = 0, multiplier = 1 } = options;
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let frameId = null;

    const update = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const elTop = rect.top;
      const elHeight = rect.height;

      // 判断是否在视口中
      const visible = elTop < vh && elTop + elHeight > 0;
      setIsVisible(visible);

      if (visible) {
        // 计算滚动进度 (0 到 1)
        const scrollProgress = clamp((-elTop + offset) / Math.max(elHeight - vh, 1), 0, 1) * multiplier;
        setProgress(scrollProgress);
      }

      frameId = null;
    };

    const handleScroll = () => {
      if (frameId) return;
      frameId = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [ref, offset, multiplier]);

  return { progress, isVisible };
}

// 动画帧管理 Hook
export function useAnimationFrame(callback, deps = []) {
  const frameRef = useRef(null);
  const lastTimeRef = useRef(performance.now());
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const start = useCallback(() => {
    if (frameRef.current) return;

    const animate = (now) => {
      const dt = Math.min(0.05, Math.max(0.001, (now - lastTimeRef.current) / 1000));
      lastTimeRef.current = now;

      const shouldContinue = callbackRef.current(now, dt);

      if (shouldContinue !== false) {
        frameRef.current = requestAnimationFrame(animate);
      } else {
        frameRef.current = null;
      }
    };

    lastTimeRef.current = performance.now();
    frameRef.current = requestAnimationFrame(animate);
  }, []);

  const stop = useCallback(() => {
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stop();
  }, [stop]);

  return { start, stop, isRunning: () => frameRef.current !== null };
}

// 鼠标追踪 Hook
export function useMouseTracker(ref, options = {}) {
  const { enabled = true } = options;
  const [mouseX, setMouseX] = useState(0);
  const [targetX, setTargetX] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    const handleMove = (e) => {
      if (isMobile() || isReduced()) return;
      const x = e.clientX / window.innerWidth - 0.5;
      setTargetX(x);
    };

    const handleLeave = () => {
      setTargetX(0);
    };

    el.addEventListener('pointermove', handleMove, { passive: true });
    el.addEventListener('pointerleave', handleLeave, { passive: true });

    return () => {
      el.removeEventListener('pointermove', handleMove);
      el.removeEventListener('pointerleave', handleLeave);
    };
  }, [ref, enabled]);

  // 平滑过渡
  useEffect(() => {
    let frameId = null;
    let lastTime = performance.now();

    const animate = (now) => {
      const dt = Math.min(0.05, (now - lastTime) / 1000);
      lastTime = now;

      setMouseX((prev) => {
        const next = damp(prev, targetX, 5.7, dt);
        if (Math.abs(targetX - next) < 0.001) return targetX;
        return next;
      });

      frameId = requestAnimationFrame(animate);
    };

    if (Math.abs(targetX - mouseX) > 0.001) {
      frameId = requestAnimationFrame(animate);
    }

    return () => {
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [targetX, mouseX]);

  return { mouseX, targetX };
}

// Intersection Observer Hook
export function useInView(ref, options = {}) {
  const { threshold = 0.18, once = true } = options;
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            if (once) observer.unobserve(entry.target);
          } else if (!once) {
            setInView(false);
          }
        });
      },
      { threshold }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, [ref, threshold, once]);

  return inView;
}

export default {
  lerp,
  clamp,
  ease,
  damp,
  isMobile,
  isReduced,
  docTop,
  useScrollProgress,
  useAnimationFrame,
  useMouseTracker,
  useInView,
};
